/**
 * SessionListCard - displays a session in the sessions list (Pro mode)
 *
 * NOTE: Session display logic is shared with other components.
 * See src/features/sessions/utils/sessionDisplayUtils.ts for shared utilities.
 */
import { useRef } from 'react';
import { Database, MoreVertical, Pencil, Server, Trash2 } from 'lucide-react';
import type { Session } from '../types/sessionTypes';
import type { Connection } from '../../../types/electron';
import { Button } from '../../../components/ui/button';
import { cn } from '../../../lib/utils';
import { ContextMenu, ContextMenuContent, ContextMenuItem, ContextMenuSeparator, ContextMenuTrigger } from '../../../components/ui/context-menu';
import { formatConnections, formatTimeAgo, getConnectionInfo } from '../utils/sessionDisplayUtils';
import { SessionNameEditor, type SessionNameEditorHandle } from './SessionNameEditor';
import { SessionTabsPreview } from './SessionTabsPreview';

interface SessionListCardProps {
  /** The session to display */
  session: Session;
  /** Saved connections to look up connection details */
  connections: Connection[];
  /** True if this is the currently active session */
  isActive: boolean;
  /** Click handler to open/switch to this session */
  onOpen: () => void;
  /** Rename handler */
  onRename?: (name: string) => void;
  /** Delete handler */
  onDelete?: () => void;
}

export function SessionListCard({
  session,
  connections,
  isActive,
  onOpen,
  onRename,
  onDelete,
}: SessionListCardProps) {
  const connectionInfo = getConnectionInfo(session.tabs, session.connections, connections);
  const nameEditorRef = useRef<SessionNameEditorHandle>(null);

  const handleActionsClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    e.currentTarget.dispatchEvent(
      new MouseEvent('contextmenu', {
        bubbles: true,
        cancelable: true,
        clientX: e.clientX,
        clientY: e.clientY,
      })
    );
  };

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div
          className={cn(
            'group flex items-center gap-3 p-3 rounded-lg border transition-colors',
            isActive
              ? 'border-primary/50 bg-primary/5'
              : 'border-border hover:bg-accent/50'
          )}
          data-testid="session-list-card"
          data-session-id={session.id}
          data-active={isActive}
        >
          {/* Active indicator */}
          <div className="flex items-center justify-center w-2">
            {isActive && (
              <div
                className="w-2 h-2 rounded-full bg-primary"
                title="Current session"
                data-testid="active-session-indicator"
              />
            )}
          </div>

          {/* Session info */}
          <div className="flex-1 min-w-0 overflow-hidden">
            <div className="flex items-center gap-2 min-w-0">
              <div className="min-w-0 flex-1">
                <SessionNameEditor
                  ref={nameEditorRef}
                  name={session.name}
                  customName={session.customName}
                  onRename={onRename}
                />
              </div>

              <span className="text-xs text-muted-foreground shrink-0">
                {formatTimeAgo(session.lastOpenedAt)}
              </span>
            </div>

            <div className="flex items-center gap-3 mt-1 text-sm text-muted-foreground flex-wrap">
              {/* Connections */}
              <div className="flex items-center gap-1.5 min-w-0">
                {connectionInfo.length > 0 && connectionInfo[0].engine === 'pgsql' ? (
                  <span className="text-xs font-medium text-[#336791] shrink-0">PG</span>
                ) : (
                  <Server className="h-3 w-3 shrink-0" />
                )}
                <span className="break-words">
                  {formatConnections(connectionInfo)}
                </span>
              </div>

              {/* Databases */}
              {connectionInfo.length > 0 && (
                <div className="flex items-center gap-1.5 shrink-0">
                  <Database className="h-3 w-3" />
                  <span>{connectionInfo.length}</span>
                </div>
              )}
            </div>

            {/* Tabs preview */}
            <SessionTabsPreview tabs={session.tabs} className="mt-2" />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-[opacity,scale] active:scale-[0.96] text-muted-foreground hover:text-foreground"
              onClick={handleActionsClick}
              data-testid="session-actions-button"
              title="Session actions"
            >
              <MoreVertical className="h-4 w-4" />
            </Button>

            {!isActive && onDelete && (
              <Button
                variant="ghost"
                size="icon"
                className="h-8 w-8 opacity-0 group-hover:opacity-100 transition-[opacity,scale] active:scale-[0.96] text-muted-foreground hover:text-destructive"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete();
                }}
                data-testid="delete-session-button"
                title="Delete session"
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}

            <Button
              variant={isActive ? 'secondary' : 'default'}
              size="sm"
              className="active:scale-[0.96] transition-[color,box-shadow,scale]"
              onClick={onOpen}
              data-testid={isActive ? 'resume-current-session-button' : 'open-session-button'}
            >
              {isActive ? 'Resume' : 'Open'}
            </Button>
          </div>
        </div>
      </ContextMenuTrigger>

      <ContextMenuContent className="w-44">
        <ContextMenuItem
          onClick={() => nameEditorRef.current?.startEditing()}
          disabled={!onRename}
          data-testid="rename-session-menu-item"
        >
          <Pencil className="mr-2 h-3.5 w-3.5" />
          <span>Rename</span>
        </ContextMenuItem>

        {onDelete && (
          <>
            <ContextMenuSeparator />
            <ContextMenuItem
              onClick={onDelete}
              className="text-destructive"
              data-testid="delete-session-menu-item"
            >
              <Trash2 className="mr-2 h-3.5 w-3.5" />
              <span>Delete</span>
            </ContextMenuItem>
          </>
        )}
      </ContextMenuContent>
    </ContextMenu>
  );
}
